import { Box, Typography } from '@mui/material';
import { Children, ReactNode, useMemo, useState } from 'react';

import { Accordion } from '@/components/Accordion/Accordion';
import { Medallion } from '@/components/display/Medallion/Medallion';
import { ClearInputTextButton } from '@/components/form/baseInputs/Button/ClearInputTextButton';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { File05Icon } from '@/components/icons/File05Icon';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { Card } from '@/components/layout/Card/Card';
import { Grid } from '@/components/layout/Grid';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import {
  DisplayTable,
  DisplayTableColumn,
  StyledTableCell,
} from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { COLORS } from '@/styles/tokens/colors';

import {
  EDUCATION_CATEGORIES,
  EDUCATION_ITEMS_BY_CATEGORY,
  EducationListItemType,
} from './Education.constants';
import { groupCustomSlidesByCategory } from './Education.utils';
import {
  CustomSlide,
  useEducationMap,
  useEducationSlideOverrides,
} from './EducationSlideOverrides/EducationSlideOverrides.context';

export interface EducationListProps {
  onClickListItem: (id: EducationListItemType) => void;
  onClickCustomSlide?: (slideIdentifier: string) => void;
}

const EDUCATION_TABLE_COLUMNS: DisplayTableColumn[] = [
  { headerName: 'Page' },
  { headerName: '', width: 40 },
];

export function EducationList({
  onClickListItem,
  onClickCustomSlide,
}: EducationListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const educationMap = useEducationMap();
  const { isSlideHidden, customSlidesMap } = useEducationSlideOverrides();
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const customSlidesByCategory = useMemo(
    () =>
      groupCustomSlidesByCategory(
        Object.fromEntries(
          Object.entries(customSlidesMap).filter(([, slide]) =>
            (slide.displayName || 'Untitled')
              .toLowerCase()
              .includes(normalizedSearch)
          )
        )
      ),
    [customSlidesMap, normalizedSearch]
  );
  const groups = EDUCATION_CATEGORIES.map((category) => ({
    category,
    items: EDUCATION_ITEMS_BY_CATEGORY[category].filter(
      (item) =>
        !isSlideHidden(item) &&
        educationMap[item].title.toLowerCase().includes(normalizedSearch)
    ),
    customSlides: customSlidesByCategory[category],
  })).filter(
    ({ items, customSlides }) => items.length > 0 || customSlides.length > 0
  );
  const hasResults =
    groups.length > 0 || customSlidesByCategory.uncategorized.length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, width: { xs: '100%', sm: 280 } }}>
        <TextInput
          label="Search articles"
          hideLabel
          placeholder="Search articles"
          inputProps={{ 'aria-label': 'Search articles' }}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          startAdornment={<SearchSmIcon size={20} color={COLORS.NAVY[700]} />}
          endAdornment={
            searchTerm ? (
              <ClearInputTextButton
                aria-label="Clear search"
                onClick={() => setSearchTerm('')}
              />
            ) : undefined
          }
          fullWidth
        />
      </Box>
      <Card
        variant={hasResults ? 'outlined' : 'transparent'}
        role={hasResults ? undefined : 'status'}
        sx={{ overflow: 'hidden' }}
      >
        {!hasResults && (
          <TableEmptyState
            text={
              searchTerm.trim()
                ? `No articles found for "${searchTerm.trim()}"`
                : 'No articles'
            }
          />
        )}
        {groups.map(({ category, items, customSlides }) => (
          <EducationGroup key={category} title={category}>
            <EducationGroupTable>
              {items.map((item) => (
                <EducationListItemDisplay
                  key={item}
                  slideId={item}
                  onClick={() => onClickListItem(item)}
                />
              ))}
              {customSlides.map((slide) => (
                <CustomSlideListItemDisplay
                  key={slide.slideIdentifier}
                  slide={slide}
                  onClick={() => onClickCustomSlide?.(slide.slideIdentifier)}
                />
              ))}
            </EducationGroupTable>
          </EducationGroup>
        ))}
        {customSlidesByCategory.uncategorized.length > 0 && (
          <EducationGroup title="Custom pages">
            <EducationGroupTable>
              {customSlidesByCategory.uncategorized.map((slide) => (
                <CustomSlideListItemDisplay
                  key={slide.slideIdentifier}
                  slide={slide}
                  onClick={() => onClickCustomSlide?.(slide.slideIdentifier)}
                />
              ))}
            </EducationGroupTable>
          </EducationGroup>
        )}
      </Card>
    </Box>
  );
}

function EducationGroupTable({ children }: { children: ReactNode }) {
  const rows = Children.toArray(children);
  const midpoint = Math.ceil(rows.length / 2);
  const columns =
    rows.length > 1 ? [rows.slice(0, midpoint), rows.slice(midpoint)] : [rows];

  return (
    <Grid container columnSpacing={4}>
      {columns.map((columnRows, columnIndex) => (
        <Grid
          item
          sm={12}
          md={columns.length === 2 ? 6 : 12}
          key={columnIndex}
          sx={{ width: '100%' }}
        >
          <DisplayTable columns={EDUCATION_TABLE_COLUMNS} hideTableHead>
            {columnRows}
          </DisplayTable>
        </Grid>
      ))}
    </Grid>
  );
}

interface EducationGroupProps {
  title: string;
  children: NonNullable<ReactNode>;
}

function EducationGroup({ title, children }: EducationGroupProps) {
  return (
    <Accordion
      variant="default"
      title={title}
      leftAdornment={
        <Medallion
          size={32}
          variant="square"
          squareRadius={8}
          colors={{
            background: COLORS.NAVY[50],
            iconColor: COLORS.NAVY[400],
          }}
          icon={<File05Icon size={24} />}
        />
      }
      isDefaultExpanded
      withDivider={false}
      disableExpandedMargin
      sx={{
        '&.MuiAccordion-root': {
          border: 0,
          borderBottom: `1px solid ${COLORS.GRAY[200]}`,
          borderRadius: '0 !important',
          padding: '0 !important',
        },
        '& .MuiAccordionSummary-root, & .MuiAccordionSummary-root.Mui-expanded':
          {
            backgroundColor: COLORS.GRAY[50],
            px: 2,
            py: 1.75,
          },
        '& .MuiAccordionDetails-root': {
          px: 2,
          pb: 1.5,
        },
        '&.MuiAccordion-root:last-of-type': {
          borderBottom: 0,
        },
      }}
    >
      {children}
    </Accordion>
  );
}

interface EducationListItemDisplayProps {
  slideId: EducationListItemType;
  onClick: () => void;
}

function EducationListItemDisplay({
  slideId,
  onClick,
}: EducationListItemDisplayProps) {
  const educationMap = useEducationMap();

  return (
    <EducationDisplayTableRow
      title={educationMap[slideId].title}
      onClick={onClick}
    />
  );
}

interface CustomSlideListItemDisplayProps {
  slide: CustomSlide;
  onClick: () => void;
}

function CustomSlideListItemDisplay({
  slide,
  onClick,
}: CustomSlideListItemDisplayProps) {
  return (
    <EducationDisplayTableRow
      title={slide.displayName || 'Untitled'}
      onClick={onClick}
    />
  );
}

interface EducationDisplayTableRowProps {
  title: string;
  onClick: () => void;
}

function EducationDisplayTableRow({
  title,
  onClick,
}: EducationDisplayTableRowProps) {
  return (
    <StyledTableRow onClick={onClick}>
      <StyledTableCell>
        <Typography variant="body-s" component="span">
          {title}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="right" sx={{ verticalAlign: 'middle' }}>
        <ChevronRightIcon
          size={16}
          color={COLORS.TEXT.SECONDARY}
          sx={{ display: 'block', ml: 'auto' }}
        />
      </StyledTableCell>
    </StyledTableRow>
  );
}
